import { Film, RotateCcw, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { MediaUploadItem } from '@/features/media';

export interface ProductMediaUploadTileProps {
  upload: MediaUploadItem;
  onCancel: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (upload: MediaUploadItem) => void;
}

/** A file still uploading, or one that failed, shown in the grid where it will land. */
export function ProductMediaUploadTile({
  upload,
  onCancel,
  onRemove,
  onRetry,
}: ProductMediaUploadTileProps) {
  const { t } = useTranslation('products');
  const isImage = upload.file.type.startsWith('image/');
  // Freed once the image has drawn; the bitmap stays on screen without the URL.
  const preview = useMemo(
    () => (isImage ? URL.createObjectURL(upload.file) : null),
    [isImage, upload.file],
  );

  const failed = upload.status === 'error';
  const percent = Math.round(upload.progress.ratio * 100);

  return (
    <li
      className={cn(
        'bg-muted relative aspect-square overflow-hidden rounded-lg border',
        failed && 'border-destructive',
      )}
      aria-label={
        failed
          ? t('media.uploadFailed', { name: upload.file.name })
          : t('media.uploading', { name: upload.file.name, percent })
      }
    >
      {preview ? (
        <img
          src={preview}
          alt=""
          className="size-full object-cover opacity-50"
          onLoad={() => URL.revokeObjectURL(preview)}
        />
      ) : (
        <div className="text-muted-foreground flex size-full items-center justify-center">
          <Film className="size-5" aria-hidden />
        </div>
      )}
      {failed ? (
        <div className="bg-background/80 absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2 text-center">
          <span className="text-destructive text-[11px] font-medium">{t('media.failedShort')}</span>
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="icon-xs"
              aria-label={t('media.retryUpload', { name: upload.file.name })}
              onClick={() => onRetry(upload)}
            >
              <RotateCcw aria-hidden />
            </Button>
            <Button
              variant="secondary"
              size="icon-xs"
              aria-label={t('media.dismissUpload', { name: upload.file.name })}
              onClick={() => onRemove(upload.id)}
            >
              <X aria-hidden />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="absolute inset-x-2 bottom-2 h-1.5 overflow-hidden rounded-full bg-black/10">
            <div
              className="bg-primary h-full transition-[width]"
              style={{ width: `${String(Math.max(percent, 4))}%` }}
            />
          </div>
          <Button
            variant="secondary"
            size="icon-xs"
            className="absolute top-1.5 right-1.5"
            aria-label={t('media.cancelUpload', { name: upload.file.name })}
            onClick={() => onCancel(upload.id)}
          >
            <X aria-hidden />
          </Button>
        </>
      )}
    </li>
  );
}
